import { AbstractService } from 'src/services/AbstractService';
import { config } from 'src/lib/config';
import { ServiceError } from 'src/lib/errors';
import { EnsembleLine } from 'src/types/ensemble';

const DISCOVERY_ACCOUNT_ID = 'ozoDdrmewShEcbUDWX8J3V';
const DISCOVERY_CONTENT_AREA_ID = 'IoBX4KKMc0uyUyA0tFQDg';
const PAGE_SIZE = 1000;

interface DiscoveryAssetItem {
    variantGroupId: string;
    designConceptId: string;
    status: string;
    lastUpdated: string;
    customProperties: {
        designUseCases: string[];
        isProminentColorVariant: boolean;
        source?: string;
        sourceTemplateToken?: string;
        legacyIdentifiers: {
            designPhysicalSpecIds: Record<string, string>;
            panels: Array<{
                name: string;
                latestTemplateToken: string;
                dtecTemplateId?: string;
                dtsTemplateId?: string;
            }>;
        };
    };
    protectedCustomProperties?: {
        strategist?: string;
    };
    derivedProperties: {
        availableCultures?: string[];
        panels: Array<{
            name: string;
            numberOfPlaceholders?: number;
            availableFinishes?: string[];
            colorComposition?: Array<{
                rgb: { r: number; g: number; b: number };
                weight: number;
            }>;
            colorVariantDifferentialColors?: Array<{
                rgb: { r: number; g: number; b: number };
                weight: number;
            }>;
        }>;
    };
    variantDimensions?: Array<{
        dimension: string;
        ordinal?: number;
        matchStrategy?: string;
        correlationId?: string;
    }>;
    id: string;
    createdBy?: string;
}

interface DiscoveryResponse {
    _embedded?: {
        item?: DiscoveryAssetItem[];
    };
}

class TemplateDiscoveryServiceApi extends AbstractService {
    public async getEnsembleLinesFromTemplateToken(
        templateToken: string,
        bearerToken: string,
        signal?: AbortSignal,
    ): Promise<EnsembleLine[]> {
        const url = `api/${this.version}/${DISCOVERY_ACCOUNT_ID}/${DISCOVERY_CONTENT_AREA_ID}/assets:ingest`;

        const searchParams = new URLSearchParams({
            'CustomProperties[legacyIdentifiers.panels.latestTemplateToken]': templateToken,
            assetKind: 'template',
            limit: PAGE_SIZE.toString(),
            requestor: config.appName,
        });

        try {
            const response = await this.api
                .get(url, {
                    searchParams,
                    signal,
                    headers: {
                        Authorization: `Bearer ${bearerToken}`,
                    },
                })
                .json<DiscoveryResponse>();

            return this.processTemplateVariants(response._embedded?.item || []);
        } catch (e) {
            throw new ServiceError({
                ...(e as Error),
                message: `Failed to fetch ensemble lines for template token ${templateToken}`,
                info: (e as Error).message,
                url,
                templateToken,
            });
        }
    }

    private processTemplateVariants(templateVariants: DiscoveryAssetItem[]): EnsembleLine[] {
        const grouped = templateVariants.reduce(
            (acc, variant) => {
                const groupId = variant.variantGroupId;

                if (!acc[groupId]) {
                    acc[groupId] = [];
                }
                acc[groupId].push(variant);
                return acc;
            },
            {} as Record<string, DiscoveryAssetItem[]>,
        );

        return Object.entries(grouped)
            .filter(([, variants]) => variants.every(
                (tv) => tv.derivedProperties?.panels?.length > 0
                        && tv.customProperties?.legacyIdentifiers?.panels?.every((p) => this.getTemplateId(p, tv)),
            ))
            .map(([, variants]) => {
                const firstTemplate = variants[0];
                const duc = firstTemplate.customProperties.designUseCases[0];

                return {
                    designUseCaseId: duc,
                    designConceptId: firstTemplate.designConceptId,
                    ensembleLineId: firstTemplate.variantGroupId,
                    ensembles: variants.map((variant) => ({
                        isDeleted: !variant.status?.toLowerCase().includes('enabled'),
                        modifiedTime: variant.lastUpdated,
                        designProperties: {
                            isProminentColorVariant: variant.customProperties.isProminentColorVariant,
                            availableCultures: variant.derivedProperties.availableCultures || [],
                        },
                        templates: variant.derivedProperties.panels.map((panel) => {
                            const legacyPanel = variant.customProperties.legacyIdentifiers.panels.find(
                                (p) => p.name === panel.name,
                            );

                            const tags: Record<string, string> = {};

                            if (variant.protectedCustomProperties?.strategist) {
                                tags.strategist = variant.protectedCustomProperties.strategist;
                            }
                            if (variant.createdBy) tags.createdBy = variant.createdBy;
                            if (variant.customProperties?.source) tags.source = variant.customProperties.source;
                            if (variant.customProperties?.sourceTemplateToken) {
                                tags.sourceTemplateToken = variant.customProperties.sourceTemplateToken;
                            }

                            return {
                                ensemblePanelType: panel.name,
                                modifiedTime: variant.lastUpdated,
                                tags,
                                designProperties: {
                                    availableFinishes: panel.availableFinishes || [],
                                    colorComposition: panel.colorComposition || [],
                                    colorVariantDifferentialColors: panel.colorVariantDifferentialColors || [],
                                    numberOfPlaceholders: panel.numberOfPlaceholders || 0,
                                },
                                designPhysicalSpecId:
                                    variant.customProperties.legacyIdentifiers.designPhysicalSpecIds[panel.name],
                                templateId: this.getTemplateId(legacyPanel!, variant),
                                templateToken: legacyPanel!.latestTemplateToken,
                            };
                        }),
                        ordinal: variant.variantDimensions?.find((d) => d.dimension === 'color')?.ordinal,
                        colorCorrelationId: variant.variantDimensions?.find(
                            (d) => d.dimension === 'color' && d.matchStrategy === 'correlationId',
                        )?.correlationId,
                    })),
                };
            });
    }

    // eslint-disable-next-line class-methods-use-this
    private getTemplateId(
        panel: { dtecTemplateId?: string; dtsTemplateId?: string; name: string },
        templateVariant: { id: string },
    ): string | null {
        if (panel.dtecTemplateId) return panel.dtecTemplateId;
        if (panel.dtsTemplateId) return panel.dtsTemplateId;
        if (templateVariant.id && panel.name) {
            return `${templateVariant.id}_${panel.name}`;
        }
        return null;
    }
}

export const TemplateDiscoveryService = new TemplateDiscoveryServiceApi(config.services.discoveryApi);
